import type { PaidPlan } from '@/types/billing-plan';

/**
 * Auto Fix usage: the pool of fixes an organization's Auto Fix loop may land per monthly period,
 * issues and CI alike, and why a pull request is paused.
 *
 * Client-safe: the configuration's types and the pure decision core. The
 * configuration itself (`config.ts`) and the ledger (`ledger.ts`) are server-only.
 */
import type { AutoFixAllowanceRule, AutoFixPlanAllowances } from './plans';

import { autoFixPoolFixes } from './plans';

import { SETTINGS_TABS } from '@/lib/constants';
import { isPaidPlan, PAID_PLANS } from '@/types/billing-plan';
import { buildSettingsPath } from '@/utils/app-paths';

type AutoFixAllowanceOverride = {
  /** Contract, pilot or ticket reference. Every exception says why. */
  note: string;
  // An absent field means "use the plan / global value".
  rule?: AutoFixAllowanceRule;
  maxRunsPerPr?: number;
  /** An ISO date. Past this instant the override is ignored (pilots end on their own). */
  expiresAt?: string;
};

export type AutoFixAllowanceConfig = {
  /**
   * When false, every run starts unmetered, the ledger reads and writes nothing, and customers
   * see no usage.
   */
  enabled: boolean;
  plans: AutoFixPlanAllowances;
  /**
   * Runs Auto Fix admits on one pull request per period, whether or not they fix anything. Not
   * usage: a cost guard, since runs that fix nothing cost the customer nothing.
   */
  maxRunsPerPr: number;
  /** Keyed by GitHub installation id. */
  overrides: Record<number, AutoFixAllowanceOverride>;
};

type AutoFixPlanKey = keyof AutoFixAllowanceConfig['plans'];

/**
 * Read by the ledger from billing. `seats` is the PR review quota's seat count: purchased seats
 * for paid plans, recent pull request authors for trials.
 */
export type AutoFixBillingSnapshot =
  | { kind: 'paid'; plan: PaidPlan; seats: number }
  | { kind: 'trial'; seats: number }
  | { kind: 'free' }
  | { kind: 'unavailable' };

type AutoFixPoolLimit =
  | { kind: 'limited'; fixes: number }
  | { kind: 'unlimited' }
  /** Billing could not be read: admit and count; never pause a customer for our outage. */
  | { kind: 'unknown' };

export type AutoFixEntitlement = {
  planKey: AutoFixPlanKey | 'unavailable';
  source: 'plan' | 'override';
  /**
   * Who sets the organization's pool: its plan, or an override's rule, which is a contract no
   * plan upgrade lifts. An override that changes only a cost guard leaves the plan's pool.
   */
  limitSource: 'plan' | 'override';
  limit: AutoFixPoolLimit;
  maxRunsPerPr: number;
};

/**
 * Why the gate refuses a pull request, with the numbers that refused it. `usedFixes` counts the
 * fixes that stand on the organization's pull requests this period.
 */
type AutoFixDenial =
  | { reason: 'not_included' }
  | { reason: 'allowance_exhausted'; usedFixes: number; limitFixes: number }
  /** The PR's admitted runs this period reached `maxRunsPerPr`, the cost guard. */
  | { reason: 'pr_run_limit'; runs: number; maxRuns: number };

/**
 * The loop's own hold on a pull request's CI fixes, not an allowance: every check still failing is
 * held (`ciHold`). A held check is one that a CI run set aside (flaky, infrastructure,
 * pre-existing, or left for a decision), and the hold gathers such checks across runs. Automatic CI
 * runs skip held checks and fix the others, so only a pull request whose failing checks are all
 * held pauses, until a person's retry runs one attempt on all of them. Dispatch decides it
 * (`toAutoFixCiHoldPause`); it never resets by itself, so `resetAt` is when the hold began, which
 * keys its one announcement.
 */
type AutoFixCiHold = { reason: 'ci_on_hold' };

/**
 * Pauses stored in loop records before fixes were pooled. Nothing decides them now: a PR allowance
 * counted PRs, and the per-PR limits on fixed issues and fixed CI checks are gone, so those pauses
 * can resume at once. The next admitted dispatch clears them.
 */
type LegacyAutoFixDenial =
  | { reason: 'allowance_exhausted'; usedPrs: number; limitPrs: number }
  | { reason: 'pr_issue_limit'; issues: number; maxIssues: number }
  | { reason: 'pr_ci_check_limit'; checkRuns: number; maxCheckRuns: number };

/**
 * The plan one step up, whose larger Auto Fix pool an organization can buy. `subscribed`: it pays
 * for a plan today, so the pricing page changes that plan instead of starting a checkout.
 */
export type AutoFixUpgrade = { plan: PaidPlan; installationId: number; subscribed: boolean };

/**
 * Why a PR is paused, frozen at the decision. Stored in loop state (ISO strings) and rendered everywhere.
 * `upgrade` is set when the organization's plan allowance refused the PR and a plan above lifts it;
 * pauses stored before upgrades were offered have none.
 */
export type AutoFixPause = { resetAt: string; upgrade?: AutoFixUpgrade } & (
  | AutoFixDenial
  | AutoFixCiHold
  | LegacyAutoFixDenial
);

/** The pause a pull request's CI hold puts on an automatic CI dispatch; `since` began the hold. */
export function toAutoFixCiHoldPause(since: string): AutoFixPause {
  return { reason: 'ci_on_hold', resetAt: since };
}

/**
 * The organization's usage this period as customers see it, on the billing page and the PR page.
 * `usedFixes` counts the fixes that stand; a run that fixed nothing uses none. A limit of 0 is a
 * plan without auto-fix, not a count; an unlimited one has no count and nothing to reset.
 * `upgrade` is the plan above, when the organization's plan sets its pool. Customer surfaces show
 * only the share used, never the count.
 */
export type AutoFixAllowanceView =
  | { kind: 'not_included'; upgrade?: AutoFixUpgrade }
  | { kind: 'unlimited' }
  | {
      kind: 'limited';
      usedFixes: number;
      limitFixes: number;
      resetAt: string;
      upgrade?: AutoFixUpgrade;
    };

/** What a run's work is: review issues, or failing CI check runs, which count as one fix a run. */
export type AutoFixWorkKind = 'issues' | 'ci_check_runs';

/** The work a run asks admission for: finding ids or check run ids, in the order it takes them. */
export type AutoFixWorkRequest = { kind: AutoFixWorkKind; ids: readonly string[] };

/**
 * An admitted run takes `ids`: the work it asked for that the pool has room for, in the order
 * asked, and reserves `reservedFixes` of the pool until it ends, the most it can land. A PR waits
 * (`wait`) while other runs' reservations fill what the pool has left: each of those runs either
 * lands its fixes or ends and frees the rest.
 */
export type AutoFixAdmissionDecision =
  | { kind: 'admit'; ids: string[]; reservedFixes: number }
  | { kind: 'wait' }
  | { kind: 'deny'; denial: AutoFixDenial };

function seatsOf(billing: AutoFixBillingSnapshot): number | null {
  return billing.kind === 'paid' || billing.kind === 'trial' ? billing.seats : null;
}

function toLimit(rule: AutoFixAllowanceRule, billing: AutoFixBillingSnapshot): AutoFixPoolLimit {
  if (rule.kind === 'per_seat' && billing.kind === 'unavailable') {
    return { kind: 'unknown' };
  }
  // Free installs have no seat count, so a per-seat override gives them nothing.
  const fixes = autoFixPoolFixes(rule, seatsOf(billing) ?? 0);
  return fixes === null ? { kind: 'unlimited' } : { kind: 'limited', fixes };
}

function activeOverride(config: AutoFixAllowanceConfig, installationId: number, now: Date) {
  const override = config.overrides[installationId];
  if (!override || (override.expiresAt !== undefined && new Date(override.expiresAt) <= now)) {
    return undefined;
  }
  return override;
}

export function resolveAutoFixEntitlement(input: {
  config: AutoFixAllowanceConfig;
  installationId: number;
  billing: AutoFixBillingSnapshot;
  now: Date;
}): AutoFixEntitlement {
  const { config } = input;
  const override = activeOverride(config, input.installationId, input.now);
  const planKey = input.billing.kind === 'paid' ? input.billing.plan : input.billing.kind;
  // An override's fixed or unlimited rule does not depend on billing, so it holds during a billing outage.
  const rule = override?.rule ?? (planKey === 'unavailable' ? null : config.plans[planKey]);
  return {
    planKey,
    source:
      override && (override.rule !== undefined || override.maxRunsPerPr !== undefined)
        ? 'override'
        : 'plan',
    limitSource: override?.rule === undefined ? 'plan' : 'override',
    limit: rule ? toLimit(rule, input.billing) : { kind: 'unknown' },
    maxRunsPerPr: override?.maxRunsPerPr ?? config.maxRunsPerPr,
  };
}

/** A PR's live claim as admission reads it: the issues whose fix stands on it, and its admitted runs. */
export type AutoFixPrClaimUsage = {
  fixedIssueIds: readonly string[];
  runs: number;
};

/** The organization's live claims this period, every PR's together, this one's included. */
export type AutoFixOrgUsage = {
  /** Fixes that stand: each fixed issue, and each CI run that fixed something. */
  usedFixes: number;
  /** What running runs may still land: the new issues each was handed, or 1 for a CI run. */
  reservedFixes: number;
};

/**
 * org: the organization's usage this period. prClaim: this PR's live claim. request: the work the
 * run would take, in the order it takes it.
 *
 * Every fix draws on the organization's pool, so any PR pauses once the fixes that stand fill it.
 * Then the PR's run cap. A run gets at most the room the pool has left after other runs'
 * reservations, and waits while they fill it, so the fixes that stand never exceed the pool.
 */
export function decideAutoFixAdmission(input: {
  entitlement: AutoFixEntitlement;
  org: AutoFixOrgUsage;
  prClaim: AutoFixPrClaimUsage | null;
  request: AutoFixWorkRequest;
}): AutoFixAdmissionDecision {
  const { entitlement, org, prClaim, request } = input;
  const { limit } = entitlement;
  if (limit.kind === 'limited' && limit.fixes === 0) {
    return { kind: 'deny', denial: { reason: 'not_included' } };
  }
  if (limit.kind === 'limited' && org.usedFixes >= limit.fixes) {
    return {
      kind: 'deny',
      denial: { reason: 'allowance_exhausted', usedFixes: org.usedFixes, limitFixes: limit.fixes },
    };
  }
  if (prClaim && prClaim.runs >= entitlement.maxRunsPerPr) {
    return {
      kind: 'deny',
      denial: { reason: 'pr_run_limit', runs: prClaim.runs, maxRuns: entitlement.maxRunsPerPr },
    };
  }
  const room =
    limit.kind === 'limited'
      ? limit.fixes - org.usedFixes - org.reservedFixes
      : Number.POSITIVE_INFINITY;
  if (room <= 0) {
    return { kind: 'wait' };
  }
  if (request.kind === 'ci_check_runs') {
    // A CI run counts as one fix however many checks it fixes, so it takes every check and reserves
    // that one fix. Dispatch asks only with failing checks to fix.
    return { kind: 'admit', ids: [...new Set(request.ids)], reservedFixes: 1 };
  }
  return takeIssues(request.ids, prClaim?.fixedIssueIds ?? [], room);
}

/**
 * The issues a run may take, in order: those the PR already counts as fixed, at no cost, and new
 * ones while the pool has room. A run cannot fix what it was not handed, so the pool holds exactly.
 */
function takeIssues(
  requestedIds: readonly string[],
  fixedIssueIds: readonly string[],
  room: number,
): AutoFixAdmissionDecision {
  const fixed = new Set(fixedIssueIds);
  const ids = new Set<string>();
  let reservedFixes = 0;
  for (const id of requestedIds) {
    if (ids.has(id)) {
      continue;
    }
    if (fixed.has(id)) {
      ids.add(id);
    } else if (reservedFixes < room) {
      ids.add(id);
      reservedFixes += 1;
    }
  }
  return { kind: 'admit', ids: [...ids], reservedFixes };
}

/**
 * The plan one step up from each plan, for more Auto Fix usage. A trial goes to Pro, because Team
 * gives as many fixes per seat as the trial. Max is the top plan. `__tests__/config.test.ts`
 * checks that every step raises the pool.
 */
export const AUTO_FIX_UPGRADE_PLANS = {
  free: 'team',
  trial: 'pro',
  team: 'pro',
  pro: 'max',
  max: null,
} as const satisfies Record<AutoFixPlanKey, PaidPlan | null>;

/**
 * The plan above the organization's, or null when no plan lifts its limit: an override's limit is a
 * contract, Max is the top plan, and an unread plan has no step up.
 */
export function resolveAutoFixUpgrade(input: {
  entitlement: Pick<AutoFixEntitlement, 'planKey' | 'limitSource'>;
  installationId: number;
}): AutoFixUpgrade | null {
  const { planKey, limitSource } = input.entitlement;
  if (limitSource === 'override' || planKey === 'unavailable') {
    return null;
  }
  const plan = AUTO_FIX_UPGRADE_PLANS[planKey];
  return plan
    ? { plan, installationId: input.installationId, subscribed: isPaidPlan(planKey) }
    : null;
}

/**
 * Whether the organization's plan allowance decided it (`allowance_exhausted`, `not_included`),
 * rather than a cost guard. Only the plan allowance grows with the plan.
 */
export function isAutoFixPlanLimit(denial: Pick<AutoFixPause, 'reason'>): boolean {
  return denial.reason === 'allowance_exhausted' || denial.reason === 'not_included';
}

/**
 * The pause a denial freezes. Only the organization's plan allowance grows with the plan, so only
 * its denials carry `upgrade`: the cost guards are the same on every plan.
 */
export function toAutoFixPause(input: {
  denial: AutoFixDenial;
  resetAt: string;
  upgrade: AutoFixUpgrade | null;
}): AutoFixPause {
  const { denial, upgrade } = input;
  const liftedByPlan = isAutoFixPlanLimit(denial);
  return { resetAt: input.resetAt, ...denial, ...(liftedByPlan && upgrade ? { upgrade } : {}) };
}

const resetDateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

const countFormat = new Intl.NumberFormat('en-US');

/** A period's reset date as the Auto Fix surfaces show it, in UTC: "Oct 14". */
/** "Nov 1": when an allowance resets, as the PR page and the fix session say it. */
export function formatAutoFixResetDate(resetAt: string): string {
  return resetDateFormat.format(new Date(resetAt));
}

export const AUTO_FIX_PAUSED_TITLE = 'Auto-fix paused';

const NOT_INCLUDED_DETAIL = "Auto-fix isn't included in your plan.";

/** What every plan-limit pause says, on every surface, without a count. */
const USED_UP_TITLE = "This month's auto-fix usage is used up";

const PR_LIMIT_TITLE = 'PR usage limit';
const CI_HOLD_TITLE = 'CI fixes paused';
/**
 * Why CI fixes are paused, as sentences without their periods. It blames no one: the failure may
 * be the customer's CI or auto-fix's own run.
 */
const CI_HOLD_DETAILS = ["Auto-fix couldn't fix the last CI failure, so it won't retry on its own"];

export function toAutoFixAllowanceView(input: {
  limit: Exclude<AutoFixPoolLimit, { kind: 'unknown' }>;
  usedFixes: number;
  resetAt: string;
  upgrade: AutoFixUpgrade | null;
}): AutoFixAllowanceView {
  const { limit } = input;
  const upgrade = input.upgrade ? { upgrade: input.upgrade } : {};
  if (limit.kind === 'unlimited') {
    return { kind: 'unlimited' };
  }
  if (limit.fixes === 0) {
    return { kind: 'not_included', ...upgrade };
  }
  return {
    kind: 'limited',
    usedFixes: input.usedFixes,
    limitFixes: limit.fixes,
    resetAt: input.resetAt,
    ...upgrade,
  };
}

/**
 * The share of the pool used, as a whole percentage: at least 1 once anything is used and at most
 * 99 while any is left, so it never reads as untouched or used up when it is not.
 */
export function autoFixUsagePercent(usage: { usedFixes: number; limitFixes: number }): number {
  const { usedFixes, limitFixes } = usage;
  if (usedFixes <= 0) {
    return 0;
  }
  if (usedFixes >= limitFixes) {
    return 100;
  }
  return Math.min(99, Math.max(1, Math.floor((usedFixes / limitFixes) * 100)));
}

/** UTM tags per surface that links an upgrade, so analytics can tell which one sold it. */
const UPGRADE_LINK_UTM = {
  check_run: { source: 'github', content: 'auto_fix_limit_check_run' },
  github_comment: { source: 'github', content: 'auto_fix_limit_comment' },
  slack: { source: 'slack', content: 'auto_fix_limit_slack' },
  mcp: { source: 'mcp', content: 'auto_fix_limit_mcp' },
  pr_page: { source: 'cubic', content: 'auto_fix_limit_sidebar' },
  code_pr_tab: { source: 'cubic', content: 'auto_fix_limit_code_pr_tab' },
  fix_session: { source: 'cubic', content: 'auto_fix_limit_fix_session' },
  action_page: { source: 'cubic', content: 'auto_fix_limit_action_page' },
  billing: { source: 'cubic', content: 'auto_fix_limit_billing' },
} as const;

export type AutoFixUpgradeLinkSource = keyof typeof UPGRADE_LINK_UTM;

/**
 * The pricing page with the upgrade's plan picked, relative to the app: a plan change for an
 * organization that pays for one, a checkout otherwise. Tagged with the linking surface.
 */
export function buildAutoFixUpgradePath(
  upgrade: AutoFixUpgrade,
  source: AutoFixUpgradeLinkSource,
): string {
  const params = new URLSearchParams({ installation_id: String(upgrade.installationId) });
  if (upgrade.subscribed) {
    params.set('mode', 'change');
  }
  params.set('plan', upgrade.plan);
  params.set('utm_source', UPGRADE_LINK_UTM[source].source);
  params.set('utm_content', UPGRADE_LINK_UTM[source].content);
  return `/pricing?${params.toString()}`;
}

/**
 * The plan that lifts a limit, with its two calls to action, both linked to
 * `buildAutoFixUpgradePath`. Every surface keeps `primary` in view: "Get more usage", or "Upgrade to
 * Team" for a plan without auto-fix, where more of none reads oddly. Only the PR page's hover
 * card shows `secondary`, never beside the primary: "Upgrade to Pro", or just "Upgrade" once the
 * primary names the plan. What the plan includes is the pricing page's to say, where people can
 * also weigh the plans above it.
 */
export type AutoFixUpgradeOffer = { primary: string; secondary: string; upgrade: AutoFixUpgrade };

function offerAutoFixUpgrade(
  upgrade: AutoFixUpgrade | undefined,
  /** The plan has no auto-fix at all, rather than none left this month. */
  planHasNone: boolean,
): AutoFixUpgradeOffer | null {
  if (!upgrade) {
    return null;
  }
  const upgradeToPlan = `Upgrade to ${PAID_PLANS[upgrade.plan].name}`;
  return planHasNone
    ? { primary: upgradeToPlan, secondary: 'Upgrade', upgrade }
    : { primary: 'Get more usage', secondary: upgradeToPlan, upgrade };
}

/**
 * Where a pause's calls to action lead: the pricing page with the plan picked, or, for someone who
 * cannot change the plan, the usage page (`usagePath`), which asks a workspace admin. Unknown
 * roles go to pricing.
 */
export function buildAutoFixUpgradeHref(params: {
  offer: AutoFixUpgradeOffer;
  source: AutoFixUpgradeLinkSource;
  canManageBilling: boolean | null | undefined;
  usagePath: string | null;
}): string {
  if (params.canManageBilling === false && params.usagePath) {
    return params.usagePath;
  }
  return buildAutoFixUpgradePath(params.offer.upgrade, params.source);
}

/** "Ask a workspace admin to upgrade to Pro.", for a member, who cannot change the plan. */
export function formatAutoFixUpgradeRequest(offer: AutoFixUpgradeOffer): string {
  return `Ask a workspace admin to upgrade to ${PAID_PLANS[offer.upgrade.plan].name}.`;
}

/** The billing page's offer: once the plan has no auto-fix, or the organization used it all. */
export function describeAutoFixAllowanceUpgrade(
  view: AutoFixAllowanceView,
): AutoFixUpgradeOffer | null {
  switch (view.kind) {
    case 'not_included':
      return offerAutoFixUpgrade(view.upgrade, true);
    case 'unlimited':
      return null;
    case 'limited':
      return view.usedFixes >= view.limitFixes ? offerAutoFixUpgrade(view.upgrade, false) : null;
  }
}

/** The allowance in one line, for hover text: the share used, never the count. */
export function describeAutoFixAllowance(view: AutoFixAllowanceView): string {
  switch (view.kind) {
    case 'not_included':
      return NOT_INCLUDED_DETAIL;
    case 'unlimited':
      return 'Unlimited auto-fix usage';
    case 'limited':
      return `${autoFixUsagePercent(view)}% of this month's auto-fix usage · resets ${formatAutoFixResetDate(view.resetAt)}`;
  }
}

/**
 * Whether the organization has auto-fix usage left now, by its live usage: an unlimited plan, or
 * fewer fixes than the pool allows. A plan without auto-fix has none, and usage nobody read (null)
 * shows none.
 */
export function hasAutoFixUsageLeft(allowance: AutoFixAllowanceView | null | undefined): boolean {
  switch (allowance?.kind) {
    case 'unlimited':
      return true;
    case 'limited':
      return allowance.usedFixes < allowance.limitFixes;
    default:
      return false;
  }
}

/**
 * Whether a pause decided by a limit that no longer exists (`LegacyAutoFixDenial`): the per-PR
 * fixed issue and CI check limits are gone, so those pauses resume at once.
 */
function isRetiredLimit(pause: AutoFixPause): boolean {
  return pause.reason === 'pr_issue_limit' || pause.reason === 'pr_ci_check_limit';
}

/**
 * Whether a retry can run. A period limit comes back at its reset. The plan's limit also comes
 * back as soon as the organization's live usage (`allowance`) has room: the plan or its seats
 * grew, or an override raised the pool. A plan without auto-fix gets none at a reset, only from a
 * plan that has some. Surfaces frozen at the pause (GitHub, Slack) pass no `allowance` and wait
 * for the reset. Admission still decides the retry: one that finds no room pauses again, and one
 * that finds other runs holding the rest waits for them.
 */
export function canRetryAutoFixPause(
  pause: AutoFixPause,
  now: Date,
  allowance?: AutoFixAllowanceView | null,
): boolean {
  // A CI hold waits for a person, who can retry at any time.
  if (isRetiredLimit(pause) || pause.reason === 'ci_on_hold') {
    return true;
  }
  if (isAutoFixPlanLimit(pause) && hasAutoFixUsageLeft(allowance)) {
    return true;
  }
  return pause.reason !== 'not_included' && now >= new Date(pause.resetAt);
}

/**
 * A pause in words, for every surface: `title` says what happened ("This month's auto-fix usage
 * is used up"), `details` say what a cost guard allows and when it comes back (none for a plan
 * without auto-fix), and `upgrade` is the plan that lifts it, none once a retry can run, since
 * the allowance is back. `allowance` is the organization's live usage on surfaces that read it
 * (`canRetryAutoFixPause`).
 */
export function describeAutoFixPause(
  pause: AutoFixPause,
  now: Date,
  allowance?: AutoFixAllowanceView | null,
): { title: string; details: string[]; upgrade: AutoFixUpgradeOffer | null } {
  const retryable = canRetryAutoFixPause(pause, now, allowance);
  const resume = retryable ? 'Ready to resume' : `Resets ${formatAutoFixResetDate(pause.resetAt)}`;
  switch (pause.reason) {
    case 'allowance_exhausted':
      return {
        title: USED_UP_TITLE,
        details: [resume],
        upgrade: retryable ? null : offerAutoFixUpgrade(pause.upgrade, false),
      };
    case 'pr_run_limit':
      return {
        title: 'PR usage limit reached',
        details: [`Up to ${countFormat.format(pause.maxRuns)} runs per PR each month`, resume],
        upgrade: null,
      };
    case 'ci_on_hold':
      return { title: CI_HOLD_TITLE, details: CI_HOLD_DETAILS, upgrade: null };
    case 'pr_issue_limit':
    case 'pr_ci_check_limit':
      // Their limits are gone, so all there is to say is that it can resume.
      return { title: AUTO_FIX_PAUSED_TITLE, details: [resume], upgrade: null };
    case 'not_included':
      // Once the plan includes auto-fix, saying it doesn't would be stale.
      return retryable
        ? { title: AUTO_FIX_PAUSED_TITLE, details: [resume], upgrade: null }
        : {
            title: "Auto-fix isn't included in your plan",
            details: [],
            upgrade: offerAutoFixUpgrade(pause.upgrade, true),
          };
  }
}

/**
 * A pause in plain sentences, for text without links (a refused retry, the MCP result, the run
 * history): "This month's auto-fix usage is used up. Resets Nov 1."
 */
export function formatAutoFixPauseText(pause: AutoFixPause, now: Date): string {
  const { title, details } = describeAutoFixPause(pause, now);
  return [title, ...details].map((sentence) => `${sentence}.`).join(' ');
}

/**
 * The usage page of the repository's installation, which shows the organization's auto-fix usage.
 * None for a cost guard, which it does not show, or when the installation is unknown, since the
 * page would show whichever organization the viewer last picked. `installationId` is the
 * repository's; without it, the pause's offer names the installation.
 */
export function buildAutoFixUsagePath(
  pause: AutoFixPause,
  installationId?: number | null,
): string | null {
  const installation = installationId ?? pause.upgrade?.installationId;
  if (!isAutoFixPlanLimit(pause)) {
    return null;
  }
  return installation === undefined
    ? null
    : buildSettingsPath({ tab: SETTINGS_TABS.USAGE, params: { installation_id: installation } });
}

type AutoFixPauseMarkdownInput = {
  pause: AutoFixPause;
  installationId?: number | null;
  now: Date;
  origin: string;
  source: AutoFixUpgradeLinkSource;
};

/** Every link to the usage page is the arrow, as in the app; GitHub shows its title on hover. */
function usageArrowMarkdown(input: AutoFixPauseMarkdownInput): string | null {
  const usagePath = buildAutoFixUsagePath(input.pause, input.installationId);
  return usagePath ? `[↗](${input.origin}${usagePath} "Auto-fix usage")` : null;
}

function upgradeMarkdown(input: AutoFixPauseMarkdownInput, upgrade: AutoFixUpgradeOffer): string {
  return `[${upgrade.primary}](${input.origin}${buildAutoFixUpgradePath(upgrade.upgrade, input.source)})`;
}

/** What happened with the arrow to the usage page beside it, then when it comes back. */
function pauseStatusMarkdown(input: AutoFixPauseMarkdownInput, title: string, details: string[]) {
  const heading = [`**${title}**`, usageArrowMarkdown(input)].filter(Boolean).join(' ');
  // A backslash ends the line without a new paragraph.
  return details.length > 0 ? `${heading}\\\n${details.join(' · ')}` : heading;
}

/**
 * The PR comment, as the action page says it: the limit reached with the arrow to the usage page
 * beside it, when it comes back, then the primary call to action.
 */
export function formatAutoFixPauseComment(input: AutoFixPauseMarkdownInput): string {
  const { title, details, upgrade } = describeAutoFixPause(input.pause, input.now);
  const status = pauseStatusMarkdown(input, title, details);
  return upgrade ? `${status}\n\n${upgradeMarkdown(input, upgrade)}` : status;
}

/**
 * The check run: its title says what happened, as GitHub's checks list shows it. Its summary says
 * it again with the arrow to the usage page beside it, as the PR comment does, then when it comes
 * back and the primary call to action.
 */
export function formatAutoFixPauseCheckRun(input: AutoFixPauseMarkdownInput): {
  title: string;
  summary: string;
} {
  return {
    title: describeAutoFixPause(input.pause, input.now).title,
    summary: formatAutoFixPauseComment(input),
  };
}

/**
 * A pause as the PR page tells it, case by case. The row under auto-fix is the next step worded
 * as one when there is one (upgrade to the plan that lifts the limit, or resume once the limit
 * reset or the organization has room again), and says what happened otherwise. The card on hover
 * says what happened and when it resets, what a cost guard allows, or why CI fixes are on hold.
 */
export type AutoFixPauseRow =
  | { kind: 'upgrade'; label: string; offer: AutoFixUpgradeOffer }
  | { kind: 'resume'; label: string }
  | { kind: 'info'; label: string };

export type AutoFixPauseCopy = {
  row: AutoFixPauseRow;
  /** What happened: the organization's usage is used up, or a cost guard was reached. */
  title: string;
  /** What the limit allows or counts, in words. Never a count: usage shows as a share. */
  body: string | null;
  /** When the limit resets, or that the plan has none; none for a CI hold, which never resets. */
  footnote: string | null;
};

/** The row is the primary call to action; the card's button is the secondary one. */
function upgradeOrInfo(offer: AutoFixUpgradeOffer | null, info: string): AutoFixPauseRow {
  return offer ? { kind: 'upgrade', label: offer.primary, offer } : { kind: 'info', label: info };
}

/** Once a retry can run, Resume is all the row says. */
function resumable(title: string, pause: AutoFixPause, now: Date): AutoFixPauseCopy {
  const hasReset = pause.reason !== 'not_included' && now >= new Date(pause.resetAt);
  return {
    row: { kind: 'resume', label: 'Resume' },
    title,
    body: null,
    // Room came back before the reset otherwise.
    footnote: hasReset ? `Reset ${formatAutoFixResetDate(pause.resetAt)}` : 'Ready to resume',
  };
}

/** A cost guard: no plan lifts it, so the row can only say what happened. */
function guardCopy(
  pause: AutoFixPause,
  now: Date,
  guard: { title: string; label: string; body: string },
): AutoFixPauseCopy {
  if (canRetryAutoFixPause(pause, now)) {
    return resumable(guard.title, pause, now);
  }
  return {
    row: { kind: 'info', label: guard.label },
    title: guard.title,
    body: guard.body,
    footnote: `Resets ${formatAutoFixResetDate(pause.resetAt)}`,
  };
}

/**
 * `allowance` is the organization's live usage, which the PR page and the fix session read: Resume
 * replaces the plan's upgrade as soon as it has room (`canRetryAutoFixPause`).
 */
export function describeAutoFixPauseCopy(
  pause: AutoFixPause,
  now: Date,
  allowance?: AutoFixAllowanceView | null,
): AutoFixPauseCopy {
  const { upgrade } = describeAutoFixPause(pause, now, allowance);
  if (isAutoFixPlanLimit(pause) && canRetryAutoFixPause(pause, now, allowance)) {
    return resumable(USED_UP_TITLE, pause, now);
  }
  switch (pause.reason) {
    case 'allowance_exhausted':
      return {
        row: upgradeOrInfo(upgrade, 'Usage used up'),
        title: USED_UP_TITLE,
        // The usage page the title's arrow opens says the rest.
        body: null,
        footnote: `Resets ${formatAutoFixResetDate(pause.resetAt)}`,
      };
    case 'not_included':
      // What each plan includes is the pricing page's to say, one click away.
      return {
        row: upgradeOrInfo(upgrade, 'Not in your plan'),
        title: "Auto-fix isn't included in your plan",
        body: null,
        footnote: 'Not in your plan',
      };
    case 'pr_run_limit':
      return guardCopy(pause, now, {
        title: PR_LIMIT_TITLE,
        label: 'PR limit',
        body: `Up to ${countFormat.format(pause.maxRuns)} runs per PR each month`,
      });
    case 'ci_on_hold':
      // The way out is the row itself; the card says why, on hover.
      return {
        row: { kind: 'resume', label: 'Retry CI' },
        title: CI_HOLD_TITLE,
        body: CI_HOLD_DETAILS.map((sentence) => `${sentence}.`).join(' '),
        footnote: null,
      };
    case 'pr_issue_limit':
    case 'pr_ci_check_limit':
      return resumable(AUTO_FIX_PAUSED_TITLE, pause, now);
  }
}
